import { mediaApi } from '@/services/mediaApi';
import { publicRegistry } from '@/i18n/dictionaries/publicRegistry';
import { useTranslation } from '@/i18n/useTranslation';
import type { VideoPlayerProps } from '@/features/common/types/VideoPlayerProps';
import '@/features/common/video-player.css';

/**
 * In-platform playback of a short video (FR-022, Clarification #13).
 *
 * The mediated media endpoint answers HTTP Range requests, so `<video>` streams progressively and
 * no streaming stack is involved — which is what makes ~50 MB the sensible cap rather than a
 * limitation to apologise for.
 *
 * This existed and was imported nowhere: the library rendered every resource as a download link
 * regardless of type, so FR-022's "vizualizare în platformă" was written, tested by nobody, and
 * delivered to no one.
 *
 * No `type` on the source, deliberately. It used to say `video/mp4`, which silently failed for
 * the webm uploads the allow-list also accepts. Omitting it lets the browser use the response's
 * own Content-Type, which MediaController already sets from the stored MIME — one fewer place for
 * the same fact to be recorded, and the one that cannot go stale.
 */
export function VideoPlayer({ mediaAssetId, title }: VideoPlayerProps) {
  const { t } = useTranslation(publicRegistry);

  return (
    <video className="video-player" controls preload="metadata" aria-label={title}>
      <source src={mediaApi.downloadUrl(mediaAssetId)} />
      {t('videoUnsupported')}
    </video>
  );
}
